import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Truck, User, Package, FileText, Plus, Loader2, 
  Eye, Trash2, CheckCircle, AlertCircle, Camera,
  FileImage, CreditCard, File, Shield
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Configuration des types de documents
const DOCUMENT_CATEGORIES = {
  vehicle: {
    title: 'Documents du véhicule',
    icon: Truck,
    iconColor: 'text-blue-600',
    bgColor: 'bg-blue-50',
    documents: [
      { type: 'carte_grise', label: 'Carte grise', inherited: true },
      { type: 'assurance', label: 'Assurance', inherited: true },
      { type: 'visite_technique', label: 'Visite technique', inherited: true }
    ]
  },
  driver: {
    title: 'Documents du chauffeur',
    icon: User,
    iconColor: 'text-purple-600',
    bgColor: 'bg-purple-50',
    documents: [
      { type: 'cni', label: 'CNI', inherited: true },
      { type: 'permis_conduire', label: 'Permis de conduire', inherited: true }
    ]
  },
  transport: {
    title: 'Documents de transport',
    icon: Package,
    iconColor: 'text-amber-600',
    bgColor: 'bg-amber-50',
    documents: [
      { type: 'loading_ticket', label: 'Bon de chargement', inherited: false },
      { type: 'unloading_ticket', label: 'Bon de déchargement', inherited: false },
      { type: 'loading_photo', label: 'Photo chargement', inherited: false, isPhoto: true },
      { type: 'unloading_photo', label: 'Photo déchargement', inherited: false, isPhoto: true },
      { type: 'transport_permit', label: 'Permis de transport', inherited: false },
      { type: 'cmr', label: 'CMR', inherited: false },
      { type: 'customs_doc', label: 'Document douanier', inherited: false }
    ]
  }
};

const DocumentItem = ({ 
  doc, 
  document, 
  onUpload, 
  onView, 
  onDelete, 
  uploading, 
  isInherited,
  inheritedDoc 
}) => {
  const inputRef = useRef(null);
  const isPresent = document || inheritedDoc;
  const displayDoc = document || inheritedDoc;
  
  return (
    <div className="flex items-center justify-between py-3 border-b last:border-b-0">
      <div className="flex items-center gap-3">
        <div className={cn(
          "w-8 h-8 rounded-full flex items-center justify-center",
          isPresent ? "bg-emerald-100" : "bg-gray-100"
        )}>
          {doc.isPhoto ? (
            <Camera className={cn("w-4 h-4", isPresent ? "text-emerald-600" : "text-gray-400")} />
          ) : (
            <FileText className={cn("w-4 h-4", isPresent ? "text-emerald-600" : "text-gray-400")} />
          )}
        </div>
        <div>
          <p className="text-sm font-medium">{doc.label}</p>
          {isPresent && displayDoc.filename && (
            <p className="text-xs text-gray-500 truncate max-w-[200px]">{displayDoc.filename}</p>
          )}
          {isInherited && isPresent && (
            <p className="text-xs text-blue-500 italic">Hérité du {doc.type.includes('cni') || doc.type.includes('permis') ? 'chauffeur' : 'véhicule'}</p>
          )}
        </div>
      </div>
      
      <div className="flex items-center gap-2">
        {isPresent ? (
          <>
            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
              <CheckCircle className="w-3 h-3 mr-1" />
              Présent
            </Badge>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onView(displayDoc)}
              className="text-blue-600 hover:text-blue-700"
            >
              <Eye className="w-4 h-4" />
            </Button>
            {!isInherited && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onDelete(displayDoc.id, doc.type)}
                className="text-red-600 hover:text-red-700"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </>
        ) : (
          <>
            <Badge variant="outline" className="text-gray-500 border-gray-300">
              <AlertCircle className="w-3 h-3 mr-1" />
              Manquant
            </Badge>
            {!isInherited && (
              <>
                <input
                  ref={inputRef}
                  type="file"
                  className="hidden"
                  accept={doc.isPhoto ? "image/*" : ".pdf,.jpg,.jpeg,.png"}
                  onChange={(e) => {
                    if (e.target.files?.[0]) {
                      onUpload(e.target.files[0], doc.type);
                      e.target.value = '';
                    }
                  }}
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => inputRef.current?.click()}
                  disabled={uploading}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  {uploading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-1" />
                      Ajouter
                    </>
                  )}
                </Button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const DocumentCategory = ({ 
  category, 
  config, 
  documents, 
  inheritedDocs,
  onUpload, 
  onView, 
  onDelete, 
  uploading 
}) => {
  const Icon = config.icon;
  
  // Get documents for this category
  const getDocumentByType = (type) => {
    const allDocs = [
      ...(documents?.loadingDocuments || []),
      ...(documents?.unloadingDocuments || []),
      ...(documents?.transportDocuments || [])
    ];
    return allDocs.find(d => d.type === type);
  };
  
  const getInheritedDoc = (type) => {
    if (!inheritedDocs) return null;
    // Map document type to inherited document field
    const mapping = {
      'carte_grise': inheritedDocs.vehicle?.registrationCard,
      'assurance': inheritedDocs.vehicle?.insurance,
      'visite_technique': inheritedDocs.vehicle?.technicalVisit,
      'cni': inheritedDocs.driver?.idCard,
      'permis_conduire': inheritedDocs.driver?.drivingLicense
    };
    return mapping[type];
  };
  
  return (
    <Card className="mb-4">
      <CardHeader className={cn("py-3", config.bgColor)}>
        <CardTitle className="flex items-center gap-2 text-base">
          <Icon className={cn("w-5 h-5", config.iconColor)} />
          {config.title}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y">
          {config.documents.map((doc) => (
            <div key={doc.type} className="px-4">
              <DocumentItem
                doc={doc}
                document={getDocumentByType(doc.type)}
                inheritedDoc={doc.inherited ? getInheritedDoc(doc.type) : null}
                isInherited={doc.inherited}
                onUpload={onUpload}
                onView={onView}
                onDelete={onDelete}
                uploading={uploading}
              />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const TransferDocumentsSection = ({ 
  transferId, 
  transfer,
  documents,
  vehicleId,
  driverId,
  onRefresh,
  onUpdate,
  compact = false 
}) => {
  const [uploading, setUploading] = useState(false);
  const [inheritedDocs, setInheritedDocs] = useState({ vehicle: null, driver: null });
  
  // Use transfer object or documents directly
  const transferDocs = transfer || documents;
  const refreshCallback = onRefresh || onUpdate;
  
  // Fetch inherited documents from vehicle and driver
  React.useEffect(() => {
    const fetchInheritedDocs = async () => {
      try {
        const vId = vehicleId || transfer?.vehicleId;
        const dId = driverId || transfer?.driverId;
        
        const vehicleDocsPromise = vId 
          ? axios.get(`${API}/vehicles/${vId}/documents`).catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] });
          
        const driverDocsPromise = dId
          ? axios.get(`${API}/drivers/${dId}/documents`).catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] });
        
        const [vehicleDocsRes, driverDocsRes] = await Promise.all([vehicleDocsPromise, driverDocsPromise]);
        
        // Transform documents into expected format
        const vehicleDocs = vehicleDocsRes.data || [];
        const driverDocs = driverDocsRes.data || [];
        
        // Build inherited docs structure
        const vehicleDocMap = {};
        vehicleDocs.forEach(doc => {
          const key = doc.documentType;
          if (key === 'carte_grise') {
            vehicleDocMap.registrationCard = { url: doc.documentUrl, filename: doc.documentName, id: doc.id };
          } else if (key === 'assurance') {
            vehicleDocMap.insurance = { url: doc.documentUrl, filename: doc.documentName, id: doc.id };
          } else if (key === 'visite_technique') {
            vehicleDocMap.technicalVisit = { url: doc.documentUrl, filename: doc.documentName, id: doc.id };
          }
        });
        
        const driverDocMap = {};
        driverDocs.forEach(doc => {
          const key = doc.documentType;
          if (key === 'cni') {
            driverDocMap.idCard = { url: doc.documentUrl, filename: doc.documentName, id: doc.id };
          } else if (key === 'permis' || key === 'permis_conduire') {
            driverDocMap.drivingLicense = { url: doc.documentUrl, filename: doc.documentName, id: doc.id };
          }
        });
        
        setInheritedDocs({
          vehicle: Object.keys(vehicleDocMap).length > 0 ? vehicleDocMap : null,
          driver: Object.keys(driverDocMap).length > 0 ? driverDocMap : null
        });
      } catch (error) {
        console.error('Error fetching inherited docs:', error);
      }
    };
    
    fetchInheritedDocs();
  }, [vehicleId, driverId, transfer?.vehicleId, transfer?.driverId]);
  
  const handleUpload = async (file, documentType) => {
    if (!file || !transferId) {
      toast.error('Erreur: ID de transfert manquant');
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', documentType);
    
    try {
      await axios.post(`${API}/contract-runs/${transferId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document uploadé avec succès');
      refreshCallback?.();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };
  
  const handleView = async (doc) => {
    if (doc?.url) {
      try {
        // If URL starts with /api, it's a local file that requires authentication
        if (doc.url.startsWith('/api')) {
          const response = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}${doc.url}`,
            {
              responseType: 'blob',
              headers: {
                'Authorization': 'Bearer cookie-auth'
              }
            }
          );
          
          // Create blob URL and open in new window
          const blob = new Blob([response.data], { type: response.headers['content-type'] });
          const blobUrl = URL.createObjectURL(blob);
          window.open(blobUrl, '_blank');
          
          // Clean up blob URL after a delay
          setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
        } else {
          // External URL, open directly
          window.open(doc.url, '_blank');
        }
      } catch (error) {
        console.error('Error viewing document:', error);
        toast.error('Erreur lors de l\'ouverture du document');
      }
    }
  };
  
  const handleDelete = async (documentId, documentType) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    
    try {
      await axios.delete(`${API}/contract-runs/${transferId}/documents/${documentId}`);
      toast.success('Document supprimé');
      refreshCallback?.();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };
  
  if (compact) {
    // Compact mode - just transport documents
    return (
      <DocumentCategory
        category="transport"
        config={DOCUMENT_CATEGORIES.transport}
        documents={transferDocs}
        inheritedDocs={inheritedDocs}
        onUpload={handleUpload}
        onView={handleView}
        onDelete={handleDelete}
        uploading={uploading}
      />
    );
  }
  
  return (
    <div className="space-y-4">
      {/* Vehicle Documents */}
      <DocumentCategory
        category="vehicle"
        config={DOCUMENT_CATEGORIES.vehicle}
        documents={transferDocs}
        inheritedDocs={inheritedDocs}
        onUpload={handleUpload}
        onView={handleView}
        onDelete={handleDelete}
        uploading={uploading}
      />
      
      {/* Driver Documents */}
      <DocumentCategory
        category="driver"
        config={DOCUMENT_CATEGORIES.driver}
        documents={transferDocs}
        inheritedDocs={inheritedDocs}
        onUpload={handleUpload}
        onView={handleView}
        onDelete={handleDelete}
        uploading={uploading}
      />
      
      {/* Transport Documents */}
      <DocumentCategory
        category="transport"
        config={DOCUMENT_CATEGORIES.transport}
        documents={transferDocs}
        inheritedDocs={inheritedDocs}
        onUpload={handleUpload}
        onView={handleView}
        onDelete={handleDelete}
        uploading={uploading}
      />
    </div>
  );
};

export default TransferDocumentsSection;
